import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDecisions } from '../lib/stores/decisions';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

export default function DecisionDetailPage() {
  const { decisionId } = useParams<{ decisionId: string }>();
  const navigate = useNavigate();
  const {
    decisions,
    options,
    criteria,
    scores,
    loading,
    error,
    initialize,
    updateDecision,
    deleteDecision,
    loadDetails,
    addOption,
    deleteOption,
    addCriterion,
    deleteCriterion,
    scoreOption,
    updateProsCons,
    initialized,
  } = useDecisions();

  const [showAddOption, setShowAddOption] = useState(false);
  const [showAddCriterion, setShowAddCriterion] = useState(false);
  const [optionTitle, setOptionTitle] = useState('');
  const [criterionForm, setCriterionForm] = useState({ name: '', weight: 5 });
  const [prosConsForm, setProsConsForm] = useState<Record<string, { pros: string[]; cons: string[] }>>({});
  const [editingProsCons, setEditingProsCons] = useState<string | null>(null);
  const [tempPros, setTempPros] = useState('');
  const [tempCons, setTempCons] = useState('');

  useEffect(() => {
    if (!initialized) initialize();
  }, [initialized]);

  useEffect(() => {
    if (decisionId && initialized) {
      loadDetails(decisionId);
    }
  }, [decisionId, initialized]);

  const decision = decisions.find(d => d.id === decisionId);
  const decisionOptions = decisionId ? (options[decisionId] || []) : [];
  const decisionCriteria = decisionId ? (criteria[decisionId] || []) : [];
  const decisionScores = decisionId ? (scores[decisionId] || []) : [];

  useEffect(() => {
    // Initialize pros-cons form from options
    const form: Record<string, { pros: string[]; cons: string[] }> = {};
    decisionOptions.forEach(opt => {
      form[opt.id] = {
        pros: opt.pros || [],
        cons: opt.cons || [],
      };
    });
    setProsConsForm(form);
  }, [decisionOptions]);

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading decision...</p>
      </div>
    );
  }

  if (!decision) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-neutral-600)', marginBottom: '1rem' }}>
          Decision not found
        </p>
        <Button onClick={() => navigate('/tools/decisions')}>
          Back to Decisions
        </Button>
      </div>
    );
  }

  const handleAddOption = async () => {
    if (optionTitle.trim() && decisionId) {
      await addOption(decisionId, optionTitle.trim());
      setOptionTitle('');
      setShowAddOption(false);
    }
  };

  const handleAddCriterion = async () => {
    if (criterionForm.name.trim() && decisionId) {
      await addCriterion(decisionId, criterionForm.name.trim(), criterionForm.weight);
      setCriterionForm({ name: '', weight: 5 });
      setShowAddCriterion(false);
    }
  };

  const handleUpdateProsCons = async (optionId: string) => {
    if (decisionId) {
      const pros = tempPros.split('\n').filter(p => p.trim());
      const cons = tempCons.split('\n').filter(c => c.trim());
      await updateProsCons(decisionId, optionId, pros, cons);
      setEditingProsCons(null);
      setTempPros('');
      setTempCons('');
    }
  };

  const handleScoreChange = async (optionId: string, criterionId: string, score: number) => {
    if (decisionId) {
      await scoreOption(decisionId, optionId, criterionId, score);
    }
  };

  const getScore = (optionId: string, criterionId: string): number => {
    const score = decisionScores.find(s => s.option_id === optionId && s.criterion_id === criterionId);
    return score?.score ?? 5;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'decided': return 'var(--color-success-600)';
      case 'implemented': return 'var(--color-primary-600)';
      default: return 'var(--color-warning-600)';
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (decisionId) {
      await updateDecision(decisionId, { status: newStatus as any });
    }
  };

  const handleDelete = async () => {
    if (decisionId && window.confirm('Delete this decision?')) {
      await deleteDecision(decisionId);
      navigate('/tools/decisions');
    }
  };

  const isProsConsMode = decision.mode === 'pros-cons';
  const topOption = decisionOptions.length > 0 ? decisionOptions.reduce((top, opt) => opt.total_score > top.total_score ? opt : top) : null;

  return (
    <div style={{ padding: '2rem', width: '100%' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/tools/decisions')}
        style={{
          marginBottom: '1.5rem',
          padding: '0.5rem 1rem',
          border: '1px solid var(--color-neutral-300)',
          borderRadius: '6px',
          background: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        ← Back to Decisions
      </button>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      {/* Decision Header */}
      <Card style={{ padding: '2rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2rem',
              fontWeight: 500,
              color: 'var(--color-neutral-900)',
              marginBottom: '0.5rem',
            }}>
              {decision.title}
            </h1>
            {decision.description && (
              <p style={{
                color: 'var(--color-neutral-600)',
                fontSize: '1rem',
                lineHeight: '1.6',
                marginBottom: '1rem',
              }}>
                {decision.description}
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: 'var(--color-neutral-100)',
                color: 'var(--color-neutral-700)',
              }}>
                {isProsConsMode ? 'Pros & Cons' : 'Decision Matrix'}
              </span>
            </div>
          </div>
          <button
            onClick={handleDelete}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid var(--color-error-300)',
              borderRadius: '6px',
              background: 'white',
              color: 'var(--color-error-600)',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Delete
          </button>
        </div>

        {/* Status Selector */}
        <div>
          <label style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--color-neutral-700)',
          }}>
            Status
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['in-progress', 'decided', 'implemented'].map(status => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                style={{
                  padding: '0.5rem 1rem',
                  border: decision.status === status ? `2px solid ${getStatusColor(status)}` : '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  background: decision.status === status ? `${getStatusColor(status)}15` : 'white',
                  color: decision.status === status ? getStatusColor(status) : 'var(--color-neutral-700)',
                  fontWeight: decision.status === status ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Recommendation (Matrix Mode Only) */}
      {!isProsConsMode && topOption && decisionCriteria.length > 0 && (
        <Card style={{
          padding: '1.5rem',
          marginBottom: '2rem',
          background: 'linear-gradient(135deg, var(--color-success-50) 0%, var(--color-success-100) 100%)',
          border: '2px solid var(--color-success-300)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🏆</span>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-success-900)' }}>
              Top Recommendation
            </h3>
          </div>
          <p style={{ fontSize: '1rem', color: 'var(--color-success-800)', marginBottom: '0.25rem' }}>
            <strong>{topOption.title}</strong>
          </p>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-success-700)' }}>
            Score: {topOption.total_score.toFixed(1)} / {decisionCriteria.reduce((sum, c) => sum + c.weight * 10, 0)}
          </p>
        </Card>
      )}

      {/* Pros-Cons Mode */}
      {isProsConsMode && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.75rem',
              fontWeight: 500,
              color: 'var(--color-neutral-900)',
            }}>
              Options ({decisionOptions.length})
            </h2>
            <Button onClick={() => setShowAddOption(!showAddOption)}>
              {showAddOption ? 'Cancel' : '+ Add Option'}
            </Button>
          </div>

          {showAddOption && (
            <Card style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
              <input
                type="text"
                value={optionTitle}
                onChange={(e) => setOptionTitle(e.target.value)}
                placeholder="Option name..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  marginBottom: '0.75rem',
                }}
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button onClick={handleAddOption} disabled={!optionTitle.trim()}>
                  Add Option
                </Button>
                <button
                  type="button"
                  onClick={() => setShowAddOption(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </Card>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
            {decisionOptions.map(option => (
              <Card key={option.id} style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-neutral-900)' }}>
                    {option.title}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      onClick={() => {
                        setEditingProsCons(option.id);
                        setTempPros((prosConsForm[option.id]?.pros || []).join('\n'));
                        setTempCons((prosConsForm[option.id]?.cons || []).join('\n'));
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-primary-600)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => decisionId && deleteOption(decisionId, option.id)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-error-600)',
                        cursor: 'pointer',
                        fontSize: '1rem',
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>

                {editingProsCons === option.id ? (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                        Pros (one per line)
                      </label>
                      <textarea
                        value={tempPros}
                        onChange={(e) => setTempPros(e.target.value)}
                        rows={4}
                        placeholder="Enter pros, one per line..."
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--color-neutral-300)',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                        Cons (one per line)
                      </label>
                      <textarea
                        value={tempCons}
                        onChange={(e) => setTempCons(e.target.value)}
                        rows={4}
                        placeholder="Enter cons, one per line..."
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--color-neutral-300)',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button onClick={() => handleUpdateProsCons(option.id)}>
                        Save
                      </Button>
                      <button
                        onClick={() => setEditingProsCons(null)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid var(--color-neutral-300)',
                          borderRadius: '6px',
                          background: 'white',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-success-700)', marginBottom: '0.5rem' }}>
                        ✓ Pros
                      </h4>
                      {prosConsForm[option.id]?.pros?.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: '1.6' }}>
                          {prosConsForm[option.id].pros.map((pro, idx) => (
                            <li key={idx} style={{ color: 'var(--color-neutral-700)' }}>{pro}</li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>
                          No pros listed
                        </p>
                      )}
                    </div>
                    <div>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-error-700)', marginBottom: '0.5rem' }}>
                        × Cons
                      </h4>
                      {prosConsForm[option.id]?.cons?.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: '1.6' }}>
                          {prosConsForm[option.id].cons.map((con, idx) => (
                            <li key={idx} style={{ color: 'var(--color-neutral-700)' }}>{con}</li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>
                          No cons listed
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Matrix Mode */}
      {!isProsConsMode && (
        <>
          {/* Criteria */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{
                fontFamily: 'var(--font-serif)',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--color-neutral-900)',
              }}>
                Criteria ({decisionCriteria.length})
              </h2>
              <Button onClick={() => setShowAddCriterion(!showAddCriterion)}>
                {showAddCriterion ? 'Cancel' : '+ Add Criterion'}
              </Button>
            </div>

            {showAddCriterion && (
              <Card style={{ marginBottom: '1rem', padding: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={criterionForm.name}
                    onChange={(e) => setCriterionForm({ ...criterionForm, name: e.target.value })}
                    placeholder="Criterion name (e.g., Cost, Ease of Use)..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '6px',
                      fontSize: '1rem',
                    }}
                  />
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-neutral-600)', marginBottom: '0.25rem' }}>
                      Weight (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={criterionForm.weight}
                      onChange={(e) => setCriterionForm({ ...criterionForm, weight: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--color-neutral-300)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button onClick={handleAddCriterion} disabled={!criterionForm.name.trim()}>
                    Add Criterion
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowAddCriterion(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '8px',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Card>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {decisionCriteria.map(criterion => (
                <div
                  key={criterion.id}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--color-neutral-300)',
                    background: 'var(--color-neutral-50)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{criterion.name}</span>
                  <span style={{
                    fontSize: '0.75rem',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '12px',
                    backgroundColor: 'var(--color-primary-100)',
                    color: 'var(--color-primary-700)',
                  }}>
                    {criterion.weight}x
                  </span>
                  <button
                    onClick={() => decisionId && deleteCriterion(decisionId, criterion.id)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-error-600)',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '0 0.25rem',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Options & Scoring */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{
                fontFamily: 'var(--font-serif)',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--color-neutral-900)',
              }}>
                Options ({decisionOptions.length})
              </h2>
              <Button onClick={() => setShowAddOption(!showAddOption)}>
                {showAddOption ? 'Cancel' : '+ Add Option'}
              </Button>
            </div>

            {showAddOption && (
              <Card style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
                <input
                  type="text"
                  value={optionTitle}
                  onChange={(e) => setOptionTitle(e.target.value)}
                  placeholder="Option name..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    marginBottom: '0.75rem',
                  }}
                />
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button onClick={handleAddOption} disabled={!optionTitle.trim()}>
                    Add Option
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowAddOption(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '8px',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Card>
            )}

            {decisionOptions.length > 0 && decisionCriteria.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-neutral-300)' }}>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: 600, color: 'var(--color-neutral-700)' }}>
                        Option
                      </th>
                      {decisionCriteria.map(criterion => (
                        <th key={criterion.id} style={{ textAlign: 'center', padding: '1rem', fontWeight: 600, color: 'var(--color-neutral-700)' }}>
                          {criterion.name}
                          <div style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--color-neutral-500)' }}>
                            ({criterion.weight}x)
                          </div>
                        </th>
                      ))}
                      <th style={{ textAlign: 'center', padding: '1rem', fontWeight: 600, color: 'var(--color-neutral-700)' }}>
                        Total
                      </th>
                      <th style={{ padding: '1rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {decisionOptions.map(option => (
                      <tr key={option.id} style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
                        <td style={{ padding: '1rem', fontWeight: 500 }}>{option.title}</td>
                        {decisionCriteria.map(criterion => (
                          <td key={criterion.id} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={getScore(option.id, criterion.id)}
                              onChange={(e) => handleScoreChange(option.id, criterion.id, parseInt(e.target.value))}
                              style={{
                                width: '60px',
                                padding: '0.5rem',
                                border: '1px solid var(--color-neutral-300)',
                                borderRadius: '6px',
                                textAlign: 'center',
                              }}
                            />
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', padding: '1rem', fontWeight: 700, fontSize: '1rem' }}>
                          {option.total_score.toFixed(1)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <button
                            onClick={() => decisionId && deleteOption(decisionId, option.id)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--color-error-600)',
                              cursor: 'pointer',
                              fontSize: '1rem',
                            }}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Card style={{ padding: '3rem', textAlign: 'center' }}>
                <p style={{ color: 'var(--color-neutral-500)' }}>
                  {decisionCriteria.length === 0
                    ? 'Add criteria first, then add options to score them.'
                    : 'Add options to start scoring.'}
                </p>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
